import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Shield, AlertTriangle, CheckCircle2, Clock, Download, Upload,
  ChevronDown, Bell, FileText, Lock, Eye, RefreshCw, Plus, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  GREEN: 'bg-[#4CAF50] text-white', AMBER: 'bg-[#FF9800] text-white',
  RED: 'bg-[#F44336] text-white', GREY: 'bg-[#9E9E9E] text-white',
};
const STATUS_BG = {
  GREEN: 'bg-[#4CAF50]/10 border-[#4CAF50]/30', AMBER: 'bg-[#FF9800]/10 border-[#FF9800]/30',
  RED: 'bg-[#F44336]/10 border-[#F44336]/30', GREY: 'bg-muted border-border',
};
const SEVERITY_STYLES = { critical: 'text-red-500 bg-red-500/10', high: 'text-red-400 bg-red-500/10', medium: 'text-amber-400 bg-amber-500/10', low: 'text-muted-foreground bg-muted' };

const POLICY_TYPES = [
  { value: 'ig', label: 'Information Governance' }, { value: 'confidentiality', label: 'Confidentiality' },
  { value: 'retention', label: 'Data Retention' }, { value: 'bcp', label: 'Business Continuity' },
  { value: 'dpa', label: 'Data Processing Agreement' }, { value: 'dsa', label: 'Data Sharing Agreement' },
  { value: 'incident', label: 'Incident Response' }, { value: 'acceptable_use', label: 'Acceptable Use' },
  { value: 'password', label: 'Password Policy' }, { value: 'remote_access', label: 'Remote Access' },
  { value: 'data_classification', label: 'Data Classification' },
];

function StatusBadge({ status }) {
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${STATUS_STYLES[status] || STATUS_STYLES.GREY}`}>{status}</span>;
}

export default function DSPTDashboard() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [tab, setTab] = useState('controls');
  const [expandedCat, setExpandedCat] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [policyDialog, setPolicyDialog] = useState(false);
  const [policyForm, setPolicyForm] = useState({ policy_type: 'ig', title: '', version: '1.0', approved_by: '', approved_date: '', review_date: '', status: 'approved' });
  const [evidenceDialog, setEvidenceDialog] = useState(false);
  const [evidenceForm, setEvidenceForm] = useState({ control_id: '', description: '' });
  const [securityEvents, setSecurityEvents] = useState([]);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => { fetchDashboard(); fetchPolicies(); }, []);

  const fetchDashboard = async () => {
    try { const { data } = await axios.get(`${API}/api/dspt/dashboard`, { withCredentials: true }); setDashboard(data); } catch { /* ignore */ }
  };

  const fetchPolicies = async () => {
    try { const { data } = await axios.get(`${API}/api/dspt/policies`, { withCredentials: true }); setPolicies(data); } catch { /* ignore */ }
  };

  const fetchSecurityEvents = async () => {
    try { const { data } = await axios.get(`${API}/api/dspt/security-events?limit=50`, { withCredentials: true }); setSecurityEvents(data); } catch { /* ignore */ }
  };

  const savePolicy = async () => {
    if (!policyForm.title) return toast.error('Title required');
    try {
      await axios.post(`${API}/api/dspt/policies`, policyForm, { withCredentials: true });
      toast.success('Policy saved'); setPolicyDialog(false); fetchPolicies(); fetchDashboard();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const recordEvidence = async () => {
    if (!evidenceForm.control_id || !evidenceForm.description) return toast.error('Fill all fields');
    try {
      await axios.post(`${API}/api/dspt/evidence`, evidenceForm, { withCredentials: true });
      toast.success('Evidence recorded'); setEvidenceDialog(false); fetchDashboard();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const acknowledgeAlert = async (id) => {
    try { await axios.put(`${API}/api/dspt/alerts/${id}/acknowledge`, {}, { withCredentials: true }); fetchDashboard(); } catch { /* ignore */ }
  };

  const runAnomalyCheck = async () => {
    try {
      const { data } = await axios.post(`${API}/api/dspt/run-anomaly-check`, {}, { withCredentials: true });
      toast.success(`Anomaly check complete: ${data.alerts_generated} alert(s)`);
      fetchDashboard();
    } catch { toast.error('Check failed'); }
  };

  const downloadPack = async () => {
    setDownloading(true);
    try {
      const resp = await axios.get(`${API}/api/dspt/export-pack`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'DSPT_Evidence_Pack.pdf';
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Evidence pack downloaded');
    } catch { toast.error('Export failed'); }
    finally { setDownloading(false); }
  };

  if (!dashboard) return <DashboardLayout><div className="flex justify-center py-20"><div className="spinner" /></div></DashboardLayout>;

  const s = dashboard.summary;
  const categories = {};
  (dashboard.controls || []).forEach(c => { if (!categories[c.category]) categories[c.category] = []; categories[c.category].push(c); });

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="dspt-dashboard">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-heading font-bold text-foreground flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#00A6A0]" />DSPT Compliance Dashboard
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">NHS Data Security and Protection Toolkit - Evidence Framework</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="border-border text-xs" onClick={runAnomalyCheck} data-testid="run-anomaly-btn">
              <RefreshCw className="w-3 h-3 mr-1" />Run Security Check
            </Button>
            <Button size="sm" variant="outline" className="border-border text-xs" onClick={downloadPack} disabled={downloading} data-testid="export-dspt-btn">
              <Download className="w-3 h-3 mr-1" />{downloading ? 'Generating...' : 'Export Evidence Pack'}
            </Button>
          </div>
        </div>

        {/* Score Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="glass-card border-border col-span-2 md:col-span-1">
            <CardContent className="p-4 text-center">
              <p className={`text-3xl font-bold ${dashboard.compliance_rate >= 80 ? 'text-[#4CAF50]' : dashboard.compliance_rate >= 50 ? 'text-[#FF9800]' : 'text-[#F44336]'}`}>{dashboard.compliance_rate}%</p>
              <p className="text-[10px] text-muted-foreground">Overall Compliance</p>
            </CardContent>
          </Card>
          {['GREEN', 'AMBER', 'RED', 'GREY'].map(st => (
            <Card key={st} className={`border ${STATUS_BG[st]}`}>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-foreground">{s[st]}</p>
                <p className="text-[10px] text-muted-foreground">{st === 'GREEN' ? 'Compliant' : st === 'AMBER' ? 'Action Needed' : st === 'RED' ? 'Failed' : 'N/A'}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Security Alerts */}
        {(dashboard.security_alerts || []).length > 0 && (
          <Card className="border-red-500/30 bg-red-500/5" data-testid="security-alerts">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Bell className="w-4 h-4 text-red-400" />
                <span className="text-sm font-medium text-foreground">Security Alerts ({dashboard.security_alerts.length})</span>
              </div>
              <div className="space-y-2">
                {dashboard.security_alerts.map(a => (
                  <div key={a.id} className="flex items-center justify-between bg-background/50 p-2 rounded-lg text-xs">
                    <div className="flex items-center gap-2 flex-1">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] ${SEVERITY_STYLES[a.severity] || ''}`}>{a.severity}</span>
                      <span className="text-foreground">{a.detail}</span>
                    </div>
                    <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => acknowledgeAlert(a.id)}>Acknowledge</Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-muted rounded-lg">
          {[{ id: 'controls', label: 'Controls' }, { id: 'policies', label: 'Policies' }, { id: 'audit', label: 'Security Audit' }].map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); if (t.id === 'audit') fetchSecurityEvents(); }}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === t.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid={`dspt-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Controls Tab */}
        {tab === 'controls' && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" variant="outline" className="border-border text-xs" onClick={() => setEvidenceDialog(true)} data-testid="record-evidence-btn">
                <Plus className="w-3 h-3 mr-1" />Record Evidence
              </Button>
            </div>
            {Object.entries(categories).map(([cat, controls]) => {
              const greens = controls.filter(c => c.status === 'GREEN').length;
              const total = controls.length;
              return (
                <div key={cat} className="border border-border rounded-xl overflow-hidden">
                  <button onClick={() => setExpandedCat(expandedCat === cat ? null : cat)} className="w-full flex items-center gap-3 p-3 bg-muted/30 hover:bg-muted/50 transition-colors text-left" data-testid={`cat-${cat.toLowerCase().replace(/\s+/g, '-')}`}>
                    <Shield className="w-4 h-4 text-[#00A6A0]" />
                    <span className="text-sm font-medium text-foreground flex-1">{cat}</span>
                    <span className="text-xs text-muted-foreground">{greens}/{total}</span>
                    <div className="flex gap-1">
                      {controls.map(c => <span key={c.id} className={`w-2 h-2 rounded-full ${STATUS_STYLES[c.status]?.split(' ')[0] || 'bg-gray-500'}`} />)}
                    </div>
                    <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${expandedCat === cat ? '' : '-rotate-90'}`} />
                  </button>
                  {expandedCat === cat && (
                    <div className="p-3 space-y-2">
                      {controls.map(c => (
                        <div key={c.id} className={`border rounded-lg p-3 ${STATUS_BG[c.status]}`} data-testid={`control-${c.id}`}>
                          <div className="flex items-start gap-2">
                            <StatusBadge status={c.status} />
                            <div className="flex-1">
                              <p className="text-xs font-medium text-foreground">[{c.id}] {c.title}</p>
                              <p className="text-[10px] text-muted-foreground mt-1">{c.evidence}</p>
                              <div className="flex gap-2 mt-1 text-[10px] text-muted-foreground">
                                <span>Type: {c.type}</span>
                                <span>Review: {c.review}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Policies Tab */}
        {tab === 'policies' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Upload organisational policies as DSPT evidence</p>
              <Button size="sm" className="btn-primary" onClick={() => setPolicyDialog(true)} data-testid="upload-policy-btn">
                <Upload className="w-4 h-4 mr-1" />Add Policy
              </Button>
            </div>
            {POLICY_TYPES.map(pt => {
              const policy = policies.find(p => p.policy_type === pt.value);
              return (
                <Card key={pt.value} className={`glass-card ${policy?.status === 'approved' ? 'border-[#4CAF50]/30' : 'border-border'}`} data-testid={`policy-${pt.value}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-foreground">{pt.label}</p>
                      {policy ? (
                        <p className="text-[10px] text-muted-foreground">{policy.title} v{policy.version} - {policy.status} {policy.approved_date ? `(${policy.approved_date})` : ''}</p>
                      ) : (
                        <p className="text-[10px] text-red-400">Not uploaded</p>
                      )}
                    </div>
                    {policy?.status === 'approved' ? <CheckCircle2 className="w-4 h-4 text-[#4CAF50]" /> : <AlertTriangle className="w-4 h-4 text-[#FF9800]" />}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Security Audit Tab */}
        {tab === 'audit' && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Recent security events from immutable audit log</p>
            {securityEvents.length === 0 ? (
              <div className="text-center py-8"><Lock className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-30" /><p className="text-sm text-muted-foreground">No security events yet</p></div>
            ) : (
              <div className="space-y-1">
                {securityEvents.map(e => (
                  <div key={e.id} className={`flex items-center gap-2 p-2 rounded-lg text-xs ${e.outcome === 'failure' ? 'bg-red-500/5 border border-red-500/20' : 'bg-muted/30'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${e.outcome === 'failure' ? 'bg-red-500' : 'bg-[#4CAF50]'}`} />
                    <span className="text-muted-foreground w-32 flex-shrink-0">{e.timestamp?.slice(0, 16)?.replace('T', ' ')}</span>
                    <span className="text-foreground font-medium w-28 flex-shrink-0">{e.event_type}</span>
                    <span className="text-foreground flex-1">{e.detail?.slice(0, 80)}</span>
                    <span className="text-muted-foreground">{e.ip_address}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Policy Dialog */}
      <Dialog open={policyDialog} onOpenChange={setPolicyDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle>Upload Policy</DialogTitle><DialogDescription>Record an organisational policy as DSPT evidence. This links to actual governance documents.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <select value={policyForm.policy_type} onChange={e => setPolicyForm(f => ({ ...f, policy_type: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm">
              {POLICY_TYPES.map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
            </select>
            <Input value={policyForm.title} onChange={e => setPolicyForm(f => ({ ...f, title: e.target.value }))} placeholder="Policy title" className="bg-muted border-border text-foreground" />
            <div className="grid grid-cols-2 gap-2">
              <Input value={policyForm.version} onChange={e => setPolicyForm(f => ({ ...f, version: e.target.value }))} placeholder="Version" className="bg-muted border-border text-foreground" />
              <select value={policyForm.status} onChange={e => setPolicyForm(f => ({ ...f, status: e.target.value }))} className="bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm">
                <option value="draft">Draft</option><option value="approved">Approved</option><option value="review">Under Review</option>
              </select>
            </div>
            <Input value={policyForm.approved_by} onChange={e => setPolicyForm(f => ({ ...f, approved_by: e.target.value }))} placeholder="Approved by" className="bg-muted border-border text-foreground" />
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[10px] text-muted-foreground">Approved date</label><Input type="date" value={policyForm.approved_date} onChange={e => setPolicyForm(f => ({ ...f, approved_date: e.target.value }))} className="bg-muted border-border text-foreground" /></div>
              <div><label className="text-[10px] text-muted-foreground">Review date</label><Input type="date" value={policyForm.review_date} onChange={e => setPolicyForm(f => ({ ...f, review_date: e.target.value }))} className="bg-muted border-border text-foreground" /></div>
            </div>
            <Button className="w-full btn-primary" onClick={savePolicy} data-testid="save-policy-btn">Save Policy</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Evidence Dialog */}
      <Dialog open={evidenceDialog} onOpenChange={setEvidenceDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle>Record Evidence</DialogTitle><DialogDescription>Confirm completion of a periodic DSPT control (e.g., audit review, access review, DR test).</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <select value={evidenceForm.control_id} onChange={e => setEvidenceForm(f => ({ ...f, control_id: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm">
              <option value="">Select control...</option>
              <option value="5.3">5.3 - Staff access review</option>
              <option value="6.4">6.4 - Audit log review</option>
              <option value="8.3">8.3 - Disaster recovery test</option>
            </select>
            <textarea value={evidenceForm.description} onChange={e => setEvidenceForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe what was done..." className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[80px]" />
            <Button className="w-full btn-primary" onClick={recordEvidence} data-testid="save-evidence-btn">Record Evidence</Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
